import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { httpService } from '../services/httpService';
import { log } from '../utils/DebugLogger';
import { validate } from '../contracts/validate';
import '../contracts/Payroll_ActivatePaymentRuleInput';
import '../contracts/Payroll_CreatePaymentRuleInput.ts';
import '../contracts/Payroll_DeactivatePaymentRuleInput';
import '../contracts/Payroll_UpdatePaymentRuleInput.ts';
import '../contracts/Payroll_ActivatePaymentRuleResponse';
import '../contracts/Payroll_CreatePaymentRuleResponse';
import '../contracts/Payroll_DeactivatePaymentRuleResponse';
import '../contracts/Payroll_DeletePaymentRuleResponse';
import '../contracts/Payroll_PaymentRulesResponse';
import '../contracts/Payroll_UpdatePaymentRuleResponse';

import type { Payroll_CreatePaymentRuleInput } from '../contracts/Payroll_CreatePaymentRuleInput';
import type { Payroll_UpdatePaymentRuleInput } from '../contracts/Payroll_UpdatePaymentRuleInput';
/**
 * Composable for payment rules management
 */
export function usePaymentRules() {
  const { t } = useI18n();
  const rules = ref([] as any[]);
  const currentRule = ref(null as any);
  const isLoading = ref(false);
  const error = ref(null);

  /**
   * List payment rules for franchises in current filter
   * Backend uses filtro_model to get selected franchises
   * @returns {Promise<Array>}
   */
  const listRules = async () => {
    isLoading.value = true;
    error.value = null;

    log('usePaymentRules', 'listRules', 'Listing rules from filter');

    try {
      const response = await httpService.get('/payroll/payment_rules', {
        responseContract: 'Payroll_PaymentRulesResponse'
      });

      if (!response.success) {
        throw new Error(response.error || 'Failed to list payment rules');
      }

      rules.value = response.dados?.items ?? [];
      log('usePaymentRules', 'listRules', 'Rules loaded', { count: rules.value.length });

      return rules.value;
    } catch (err) {
      error.value = err.message || 'An unexpected error occurred';
      log('usePaymentRules', 'listRules', 'ERROR', { error: err.message });
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * List rules by user and franchise (employee-centered architecture)
   * @param {number} franchiseId - Franchise ID
   * @param {string} userId - Employee Cognito sub
   * @returns {Promise<Array>}
   */
  const listRulesByUserAndFranchise = async (franchiseId: any, userId: any) => {
    log('usePaymentRules', 'listRulesByUserAndFranchise', 'Listing rules', { franchiseId, userId });

    try {
      // First list all rules, then filter client-side
      // Backend returns rules filtered by current user's franchise access
      const allRules = await listRules();

      const filteredRules = allRules.filter((rule: any) => {
        const matchesFranchise = Number(rule.franchise_id) === Number(franchiseId);
        const matchesUser = rule.user_id === userId;
        const isActive = rule.active === 1 || rule.active === '1' || rule.active === true;
        return matchesFranchise && matchesUser && isActive;
      });

      log('usePaymentRules', 'listRulesByUserAndFranchise', 'Filtered rules', {
        total: allRules.length,
        filtered: filteredRules.length
      });

      return filteredRules;
    } catch (err) {
      log('usePaymentRules', 'listRulesByUserAndFranchise', 'ERROR', { error: err.message });
      throw err;
    }
  };

  /**
   * @deprecated Use listRulesByUserAndFranchise instead (employee-centered architecture)
   * Legacy function for frequency-based filtering - now just returns rules for franchise
   */
  const listRulesByFranchiseAndFrequency = async (franchiseId: any, frequency: any) => {
    log('usePaymentRules', 'listRulesByFranchiseAndFrequency', 'DEPRECATED - use listRulesByUserAndFranchise', { franchiseId, frequency });

    try {
      const allRules = await listRules();

      // Filter by franchise only (frequency is deprecated in employee-centered architecture)
      const filteredRules = allRules.filter((rule: any) => {
        const matchesFranchise = Number(rule.franchise_id) === Number(franchiseId);
        const isActive = rule.active === 1 || rule.active === '1' || rule.active === true;
        return matchesFranchise && isActive;
      });

      return filteredRules;
    } catch (err) {
      log('usePaymentRules', 'listRulesByFranchiseAndFrequency', 'ERROR', { error: err.message });
      throw err;
    }
  };

  /**
   * Create new payment rule
   * @param {Object} ruleData - { user_id, franchise_id, rule_type, amount, meta }
   * @returns {Promise<Object>}
   */
  const createRule = async (ruleData: Payroll_CreatePaymentRuleInput) => {
    const timestamp = new Date().toISOString();
    const callId = Math.random().toString(36).substring(7);

    isLoading.value = true;
    error.value = null;

    log('usePaymentRules', 'createRule', `Creating rule [${callId}]`, { ruleData });

    try {
      // Validate before sending
      const validationErrors = validateRule(ruleData);
      if (validationErrors.length > 0) {
        throw new Error(validationErrors.join(', '));
      }

      validate('Payroll_CreatePaymentRuleInput', ruleData);
      const response = await httpService.post('/payroll/create_payment_rule', ruleData, {
        responseContract: 'Payroll_CreatePaymentRuleResponse'
      });

      if (!response.success) {
        throw new Error(response.error || 'Failed to create payment rule');
      }

      if (!response.dados) {
        throw new Error(response.error || 'Empty create payment rule response');
      }
      currentRule.value = response.dados;
      log('usePaymentRules', 'createRule', 'Rule created', { ruleId: currentRule.value.id });

      return currentRule.value;
    } catch (err) {
      error.value = err.message || 'An unexpected error occurred';
      log('usePaymentRules', 'createRule', 'ERROR', { error: err.message });
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Update payment rule (only amount, description, and meta allowed)
   * @param {number} ruleId
   * @param {Object} data - { amount?, description?, meta? }
   * @returns {Promise<Object>}
   */
  const updateRule = async (ruleId: any, data: Payroll_UpdatePaymentRuleInput) => {
    isLoading.value = true;
    error.value = null;

    log('usePaymentRules', 'updateRule', 'Updating rule', { ruleId, data });

    try {
      validate('Payroll_UpdatePaymentRuleInput', data);
      const response = await httpService.put(`/payroll/update_payment_rule/${ruleId}`, data, {
        responseContract: 'Payroll_UpdatePaymentRuleResponse'
      });

      if (!response.success) {
        throw new Error(response.error || 'Failed to update payment rule');
      }

      if (!response.dados) {
        throw new Error(response.error || 'Empty update payment rule response');
      }
      currentRule.value = response.dados;
      log('usePaymentRules', 'updateRule', 'Rule updated', { ruleId });

      return currentRule.value;
    } catch (err) {
      error.value = err.message || 'An unexpected error occurred';
      log('usePaymentRules', 'updateRule', 'ERROR', { error: err.message });
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Deactivate payment rule
   * @param {number} ruleId
   * @returns {Promise<boolean>}
   */
  const deactivateRule = async (ruleId: number) => {
    isLoading.value = true;
    error.value = null;

    log('usePaymentRules', 'deactivateRule', 'Deactivating rule', { ruleId });

    try {
      const response = await httpService.post(`/payroll/deactivate_payment_rule/${ruleId}`, null, {
        inputContract: 'Payroll_DeactivatePaymentRuleInput',
        responseContract: 'Payroll_DeactivatePaymentRuleResponse'
      });

      if (!response.success) {
        throw new Error(response.error || 'Failed to deactivate payment rule');
      }

      log('usePaymentRules', 'deactivateRule', 'Rule deactivated', { ruleId });
      return true;
    } catch (err) {
      error.value = err.message || 'An unexpected error occurred';
      log('usePaymentRules', 'deactivateRule', 'ERROR', { error: err.message });
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Activate payment rule
   * @param {number} ruleId
   * @returns {Promise<boolean>}
   */
  const activateRule = async (ruleId: number) => {
    isLoading.value = true;
    error.value = null;

    log('usePaymentRules', 'activateRule', 'Activating rule', { ruleId });

    try {
      const response = await httpService.post(`/payroll/activate_payment_rule/${ruleId}`, null, {
        inputContract: 'Payroll_ActivatePaymentRuleInput',
        responseContract: 'Payroll_ActivatePaymentRuleResponse'
      });

      if (!response.success) {
        throw new Error(response.error || 'Failed to activate payment rule');
      }

      log('usePaymentRules', 'activateRule', 'Rule activated', { ruleId });
      return true;
    } catch (err) {
      error.value = err.message || 'An unexpected error occurred';
      log('usePaymentRules', 'activateRule', 'ERROR', { error: err.message });
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Delete payment rule permanently
   * @param {number} ruleId
   * @returns {Promise<boolean>}
   */
  const deleteRule = async (ruleId: number) => {
    isLoading.value = true;
    error.value = null;

    log('usePaymentRules', 'deleteRule', 'Deleting rule', { ruleId });

    try {
      const response = await httpService.delete(`/payroll/delete_payment_rule/${ruleId}`, {
        responseContract: 'Payroll_DeletePaymentRuleResponse'
      });

      if (!response.success) {
        throw new Error(response.error || 'Failed to delete payment rule');
      }

      log('usePaymentRules', 'deleteRule', 'Rule deleted', { ruleId });
      return true;
    } catch (err) {
      error.value = err.message || 'An unexpected error occurred';
      log('usePaymentRules', 'deleteRule', 'ERROR', { error: err.message });
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Validate commission structure brackets
   * @param {Array} brackets - Array of bracket objects { min, max, rate }
   * @param {string} structureType - 'tiered' or 'progressive'
   * @returns {Array<string>} - Array of error messages
   */
  const validateBrackets = (brackets: any, structureType: any) => {
    const errors = [];

    // Must have at least 2 brackets for structured commission
    if (!brackets || brackets.length < 2) {
      errors.push(t('Estrutura de comissão deve ter pelo menos 2 faixas'));
      return errors;
    }

    // Sort brackets by min value for validation
    const sortedBrackets = [...brackets].sort((a, b) => (a.min || 0) - (b.min || 0));

    // Validate each bracket
    for (let i = 0; i < sortedBrackets.length; i++) {
      const bracket = sortedBrackets[i];
      const bracketNum = i + 1;

      // Validate min value
      if (bracket.min === null || bracket.min === undefined || bracket.min < 0) {
        errors.push(t(`Faixa ${bracketNum}: Valor mínimo inválido`));
      }

      // Validate max value (null/0 is allowed for last bracket only)
      // Note: InputNumber may set empty to 0 instead of null
      const maxIsEmpty = bracket.max === null || bracket.max === undefined || bracket.max === 0;

      if (i < sortedBrackets.length - 1) {
        // Not the last bracket - max is required and must be > min
        if (maxIsEmpty) {
          errors.push(t(`Faixa ${bracketNum}: Valor máximo é obrigatório (exceto última faixa)`));
        } else if (bracket.max <= bracket.min) {
          errors.push(t(`Faixa ${bracketNum}: Valor máximo deve ser maior que o mínimo`));
        }
      } else {
        // Last bracket - max should be null/empty (unlimited)
        if (!maxIsEmpty) {
          errors.push(t('Última faixa deve ter valor máximo vazio (ilimitado)'));
        }
      }

      // Validate rate
      if (bracket.rate === null || bracket.rate === undefined) {
        errors.push(t(`Faixa ${bracketNum}: Taxa é obrigatória`));
      } else if (bracket.rate < 0 || bracket.rate > 1.0) {
        errors.push(t(`Faixa ${bracketNum}: Taxa deve estar entre 0% e 100% (0.00 a 1.00)`));
      }

      // Check for gaps or overlaps with next bracket
      // Allow for natural boundary: max 29999.99 → min 30000 is valid (0.01 difference)
      if (i < sortedBrackets.length - 1) {
        const nextBracket = sortedBrackets[i + 1];
        const currentMax = parseFloat(bracket.max);
        const nextMin = parseFloat(nextBracket.min);
        const tolerance = 0.02; // Allow 0.01 gap for currency boundaries

        // Gap: nextMin is more than tolerance away from currentMax
        if (nextMin - currentMax > tolerance) {
          errors.push(t(`Há uma lacuna entre faixa ${bracketNum} (max: ${currentMax}) e faixa ${bracketNum + 1} (min: ${nextMin})`));
        }
        // Overlap: nextMin is less than or equal to currentMax
        else if (nextMin <= currentMax) {
          errors.push(t(`Há sobreposição entre faixa ${bracketNum} (max: ${currentMax}) e faixa ${bracketNum + 1} (min: ${nextMin})`));
        }
      }
    }

    // First bracket should start at 0
    if (sortedBrackets[0].min !== 0) {
      errors.push(t('Primeira faixa deve começar em 0'));
    }

    return errors;
  };

  /**
   * Validate payment rule data
   * @param {Object} ruleData
   * @returns {Array<string>} - Array of error messages
   */
  const validateRule = (ruleData: any) => {
    const errors = [];

    // Required fields
    if (!ruleData.user_id) errors.push('User is required');
    if (!ruleData.franchise_id) errors.push('Franchise is required');
    if (!ruleData.rule_type) errors.push('Rule type is required');
    if (ruleData.amount === null || ruleData.amount === undefined) errors.push('Amount is required');

    // For FIXED rules, validate meta contains period_type
    if (ruleData.rule_type === 'fixed') {
      if (ruleData.amount < 0) {
        errors.push('Fixed salary cannot be negative');
      }
      // Validate meta contains period_type
      if (ruleData.meta) {
        try {
          const meta = typeof ruleData.meta === 'string' ? JSON.parse(ruleData.meta) : ruleData.meta;
          if (!meta.period_type) {
            errors.push('Period type is required for fixed salary rules');
          }
        } catch (e) {
          errors.push(t('Meta field contém JSON inválido'));
        }
      } else {
        errors.push('Period type is required for fixed salary rules');
      }
    }

    // Validate meta field for commission structure if present
    if (ruleData.meta) {
      try {
        const meta = typeof ruleData.meta === 'string' ? JSON.parse(ruleData.meta) : ruleData.meta;

        if (meta.commission_structure) {
          const { type, brackets } = meta.commission_structure;

          // Validate structure type
          if (!['tiered', 'progressive'].includes(type)) {
            errors.push(t('Tipo de estrutura deve ser "tiered" ou "progressive"'));
          }

          // Validate brackets
          if (brackets) {
            const bracketErrors = validateBrackets(brackets, type);
            errors.push(...bracketErrors);
          } else {
            errors.push(t('Estrutura de comissão deve conter faixas (brackets)'));
          }
        }
      } catch (e) {
        errors.push(t('Meta field contém JSON inválido'));
      }
    }

    // Commission percentage validation (for simple commission without structure)
    if (ruleData.rule_type && ruleData.rule_type.includes('commission') && !ruleData.meta) {
      if (ruleData.amount < 0 || ruleData.amount > 1.0) {
        errors.push('Commission must be between 0% and 100% (0.00 to 1.00)');
      }
    }

    return errors;
  };

  /**
   * Get rule type label for display
   * @param {string} ruleType
   * @returns {string}
   */
  const getRuleTypeLabel = (ruleType: any) => {
    const labels = {
      fixed: t('Fixo'),
      commission_over_gross_revenue_executed: t('Comissão s/ executado'),
      commission_over_gross_revenue_sold_remotely: t('Comissão s/ vendido remoto'),
      commission_over_gross_revenue_sold_in_loco: t('Comissão s/ vendido presencial')
    };
    return (labels as any)[ruleType] || ruleType;
  };

  return {
    // State
    rules,
    currentRule,
    isLoading,
    error,

    // Methods
    listRules,
    listRulesByUserAndFranchise,
    listRulesByFranchiseAndFrequency, // Deprecated - for backward compatibility
    createRule,
    updateRule,
    activateRule,
    deactivateRule,
    deleteRule,
    validateRule,
    validateBrackets,

    // Helpers
    getRuleTypeLabel,
    getFrequencyLabel: () => '' // Deprecated - frequency no longer used in employee-centered architecture
  };
}
